/**
 * Unauthorized User Screen
 * Displays user information when authentication succeeds but user is not whitelisted
 */

import { useState } from 'react';
import { LockKeyhole, Copy, Check, Mail, LogOut, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/AuthProvider';

export interface UnauthorizedUser {
  id: string;
  email: string | null;
  displayName: string;
  spotifyUrl: string;
}

export interface UnauthorizedUserScreenProps {
  user: UnauthorizedUser;
}

export function UnauthorizedUserScreen({ user }: UnauthorizedUserScreenProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState(user.email || '');

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const mailtoLink = `mailto:mhavo@iki.fi?subject=Splister Access Request&body=Hi,%0A%0AI would like to request access to Splister.%0A%0AUser ID: ${user.id}%0AEmail: ${userEmail || '(please add your email)'}%0A%0AThank you!`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-6xl font-bold text-white mb-2">Splister</h1>
          <p className="text-gray-400 text-lg">
            Smart Playlist Management for Spotify
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-gray-800 rounded-lg shadow-2xl overflow-hidden">
          {/* Header with Lock Icon */}
          <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-b border-amber-600/30 px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="bg-amber-500/10 p-3 rounded-lg border border-amber-500/20">
                <LockKeyhole className="w-8 h-8 text-amber-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Account Not Authorized
                </h2>
                <p className="text-amber-200/80">
                  Splister is currently in alpha testing
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6 space-y-6">
            {/* Explanation */}
            <div className="text-gray-300">
              <p className="mb-3">
                Your Spotify login was successful, but your account needs to be
                added to the whitelist before you can use Splister.
              </p>
              <p className="text-gray-400 text-sm">
                Please share the information below with the administrator to
                request access.
              </p>
            </div>

            {/* User Details Card */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                  Your Account Details
                </h3>
              </div>
              <div className="divide-y divide-gray-700">
                <InfoRow
                  label="Display Name"
                  value={user.displayName}
                  onCopy={() => copyToClipboard(user.displayName, 'displayName')}
                  copied={copiedField === 'displayName'}
                />
                <InfoRow
                  label="User ID"
                  value={user.id}
                  onCopy={() => copyToClipboard(user.id, 'id')}
                  copied={copiedField === 'id'}
                  highlight
                />
                {user.email ? (
                  <InfoRow
                    label="Email"
                    value={user.email}
                    onCopy={() => copyToClipboard(user.email!, 'email')}
                    copied={copiedField === 'email'}
                  />
                ) : (
                  <EmailInputRow
                    value={userEmail}
                    onChange={setUserEmail}
                  />
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-emerald-200 text-sm font-medium mb-2">
                How to request access:
              </p>
              <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
                {!user.email && (
                  <li>Enter your email address above (find it in your Spotify profile)</li>
                )}
                <li>Click "Contact Administrator" — your details will be pre-filled</li>
                <li>Send the email and wait for confirmation (usually within 24 hours)</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href={mailtoLink}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                <Mail className="w-5 h-5" />
                Contact Administrator
              </a>
              <button
                onClick={handleLogout}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>Questions? Contact mhavo@iki.fi</p>
        </div>
      </div>
    </div>
  );
}

/**
 * InfoRow - Copyable information row with label, value, and copy button
 */
interface InfoRowProps {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
  highlight?: boolean;
}

function InfoRow({
  label,
  value,
  onCopy,
  copied,
  highlight = false,
}: InfoRowProps) {
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-gray-800/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
          {label}
        </div>
        <div
          className={`
            font-medium truncate
            ${
              highlight
                ? 'font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20 inline-block'
                : 'text-gray-200'
            }
          `}
          title={value}
        >
          {value}
        </div>
      </div>
      <button
        onClick={onCopy}
        className="flex-shrink-0 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
        title={`Copy ${label}`}
        aria-label={`Copy ${label} to clipboard`}
      >
        {copied ? (
          <Check className="w-5 h-5 text-emerald-500" />
        ) : (
          <Copy className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}

/**
 * EmailInputRow - Email input field with link to Spotify profile
 */
interface EmailInputRowProps {
  value: string;
  onChange: (value: string) => void;
}

function EmailInputRow({ value, onChange }: EmailInputRowProps) {
  return (
    <div className="px-4 py-3 hover:bg-gray-800/50 transition-colors">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="text-xs text-gray-500 uppercase tracking-wide">
          Email Address
        </div>
        <a
          href="https://www.spotify.com/account/profile/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
          title="Find your email in Spotify account settings"
        >
          <span>Find in Spotify</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <input
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="your.email@example.com"
        className="w-full bg-gray-700 text-gray-200 px-3 py-2 rounded border border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-colors"
      />
      <p className="text-xs text-gray-500 mt-2">
        We need your email to contact you about access. You can find it in your{' '}
        <a
          href="https://www.spotify.com/account/profile/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-400 hover:text-emerald-300 underline"
        >
          Spotify account profile
        </a>.
      </p>
    </div>
  );
}
